import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Summary.css';
import { getAssetTypes, getPinnedAssetSummary, getSitesLite, getPlantsBySite, getAccountsLite, CustomerAccountLite, AssetSummaryItem, AlertItem, getGatewayStatus, parsePaginatedSummaryResponse, PaginatedAssetSummaryResponse } from '../../Services/serviceURL';
import DangerRedIcon from '../../Images/Danger-red-icon.png';
import WarningYellowIcon from '../../Images/Warning-yellow-icon.png';
import { CustomerListResponse } from '../CustomerOnboard/OnboardedCustomertableService';
import { CustomerLite } from '../Plants/CreatePlant';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchPlantsBySite, fetchAssetSummary, setPinnedAssetsMap, setPinnedAssetsMapByCustomer, PinnedAssetsMap, selectPinnedAssetsMap, pinAsset, loadPinnedAssetsFromStorage, buildSummaryCacheKey, loadAssetSummaryFromCache, clearAssetSummaryPageCache } from './SummarySlice';
import useSearch from '../../hooks/useSearch';
import pinWhite from '../../Images/pin-01.svg';
import pinColor from '../../Images/pin-clr.svg';
import apiInstance from '../../config/HttpCommon';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from 'react-redux';
import store, { RootState } from '../../store';
import { decodeToken, isTokenExpired, refershTokenApi } from '../../Services/Users.service';
import { logout } from '../../Login/authslice';
import DownloadIcon from '../../Images/download-icon.svg';
import ArrowUp from '../../Images/chevron-up.svg';
import ArrowDown from '../../Images/chevron-down.svg';
import ArrowDefault from '../../Images/Chevron-default-icon.svg';
import SearchIcon from '../../Images/search-icon.svg';
import InfoIcon from '../../Images/info-circle.svg';
import Loader from '../../Components/common/loader/loader';
import MapView from '../Summary/MapView';

type SortableKeys = 'customer' | 'site' | 'plant' | 'assetType' | 'assetName' | 'status' | 'alertsCount' | 'lastCommunication' |'firstCommunication' |'ioTDeviceConnectionState';

// Utility function to format last communication date
const formatLastCommunication = (dateString: string | null | undefined): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid date

    // Format as a more readable date string
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return dateString; // Return original string if parsing fails
  }
};


const formatFirstCommunication = (dateString: string | null | undefined): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid date

    // Format as a more readable date string
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return dateString; // Return original string if parsing fails
  }
};

// Utility function to format alert time
const formatAlertTime = (alertTime: string | null | undefined): string => {
  if (!alertTime) return '';

  try {
    const date = new Date(alertTime);
    if (isNaN(date.getTime())) return alertTime;

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return alertTime || '';
  }
};

// Utility function to get severity class
const getSeverityClass = (severity: string | null | undefined): string => {
  if (!severity) return 'info'; // Default to 'info' if severity is null, undefined, or empty

  const severityLower = severity.toLowerCase();
  switch (severityLower) {
    case 'critical':
    case 'high':
      return 'critical';
    case 'warning':
    case 'medium':
      return 'warning';
    case 'info':
    case 'low':
      return 'info';
    default:
      return 'info';
  }
};

type ExtractedSite = {
  siteId: string;
  site: string;
  customerId: string;
  customerName: string;
};

type ExtractedPlant = {
  plantId: string;
  plantName: string;
  siteId: string;
  siteName: string;
  customerId: string;
  customerName: string;
};

const mergeSitesFromAssets = (assets: AssetSummaryItem[], existing: ExtractedSite[]): ExtractedSite[] => {
  const siteMap = new Map(existing.map((site) => [site.siteId, site]));

  assets.forEach((asset) => {
    if (asset.siteId && asset.siteName && !siteMap.has(asset.siteId)) {
      siteMap.set(asset.siteId, {
        siteId: asset.siteId,
        site: asset.siteName,
        customerId: asset.customerId || '',
        customerName: asset.customerName || '',
      });
    }
  });

  return Array.from(siteMap.values()).sort((a, b) => {
    const customerCompare = a.customerName.localeCompare(b.customerName);
    if (customerCompare !== 0) return customerCompare;
    return a.site.localeCompare(b.site);
  });
};

const mergePlantsFromAssets = (assets: AssetSummaryItem[], existing: ExtractedPlant[]): ExtractedPlant[] => {
  const plantMap = new Map(existing.map((plant) => [plant.plantId, plant]));

  assets.forEach((asset) => {
    if (asset.plantId && asset.plantName && !plantMap.has(asset.plantId)) {
      plantMap.set(asset.plantId, {
        plantId: asset.plantId,
        plantName: asset.plantName,
        siteId: asset.siteId || '',
        siteName: asset.siteName || '',
        customerId: asset.customerId || '',
        customerName: asset.customerName || '',
      });
    }
  });

  return Array.from(plantMap.values()).sort((a, b) => {
    const siteCompare = a.siteName.localeCompare(b.siteName);
    if (siteCompare !== 0) return siteCompare;
    return a.plantName.localeCompare(b.plantName);
  });
};

const countPinnedAssetsInMap = (
  mapByCustomer: Record<string, PinnedAssetsMap>,
  customerId?: string
): number => {
  if (customerId) {
    return Object.keys(mapByCustomer[customerId] || {}).length;
  }
  return Object.values(mapByCustomer).reduce(
    (total, customerMap) => total + Object.keys(customerMap).length,
    0
  );
};

const Summary: React.FC = () => {
  const navigate = useNavigate();
  
  // Get user details from auth state (similar to UserPreferences.tsx)
  const userId = useSelector((state: RootState) => state.auth.userId);
  const userRole = useSelector((state: RootState) => state.auth.userRole);
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
  
  // Extract user info from token (similar to UserPreferences.tsx)
  const extractUserInfoFromToken = () => {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      try {
        const decoded = decodeToken(accessToken) as any;
        
        const userInfo = {
          email: decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
          name: decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
          role: decoded?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
          userType: decoded?.UserType,
          userId: decoded?.UserId
        };
        
        return userInfo;
      } catch (error) {
        return null;
      }
    }
    return null;
  };
  
  const tokenUserInfo = extractUserInfoFromToken();
  const [extractedSites, setExtractedSites] = useState<ExtractedSite[]>([]);

  const [extractedPlants, setExtractedPlants] = useState<ExtractedPlant[]>([]);
 

  // Get the current user ID for pin/unpin operations
  const getCurrentUserId = (): string => {
    // Try multiple sources for user ID (similar to UserPreferences.tsx)
    const userIdToUse = tokenUserInfo?.userId || 
                       tokenUserInfo?.email || 
                       userId || 
                       localStorage.getItem('loginemail') || 
                       localStorage.getItem('userId') ||
                       'unknown-user'; // fallback
    
    return userIdToUse;
  };
  const [isLoadingSites, setIsLoadingSites] = useState(false);

  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [sites, setSites] = useState<{ siteId: string; site: string }[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [selectedPlantId, setSelectedPlantId] = useState<string>('');
  const [assetTypes, setAssetTypes] = useState<any[]>([]);
  const [selectedAssetType, setSelectedAssetType] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{ key: SortableKeys; direction: 'asc' | 'desc' } | null>(null);
  // Searchable dropdown UI state
  const [isCustomerOpen, setIsCustomerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [isSiteOpen, setIsSiteOpen] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  const [isPlantOpen, setIsPlantOpen] = useState(false);
  const [plantSearch, setPlantSearch] = useState('');
  const [isAssetTypeOpen, setIsAssetTypeOpen] = useState(false);
  const [assetTypeSearch, setAssetTypeSearch] = useState('');
  const [showAlertsOnly, setShowAlertsOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pinnedPageItems, setPinnedPageItems] = useState<any[]>([]);
  const [pinnedTotalCount, setPinnedTotalCount] = useState(0);
  const [pinnedTotalPages, setPinnedTotalPages] = useState(0);
  const [pinnedPageLoading, setPinnedPageLoading] = useState(false);
  const [pinnedInitDone, setPinnedInitDone] = useState(false);
  const [activeTab, setActiveTab] = useState<'assets' | 'pinned' | 'mapview'>('assets');
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [showGatewayStatusModal, setShowGatewayStatusModal] = useState(false);
  const [clientFilteredAssets, setClientFilteredAssets] = useState<any[]>([]);
  const [selectedAssetAlerts, setSelectedAssetAlerts] = useState<{
    assetName: string;
    alerts: AlertItem[];
  } | null>(null);
  const [selectedGatewayStatus, setSelectedGatewayStatus] = useState<{
    assetName: string;
    status: string;
    loading: boolean;
    error: string | null;
  } | null>(null);
  const pinnedAssetsMapByCustomer = useSelector(
    (state: RootState) => state.summary.pinnedAssetsMapByCustomer
  );

  const LOCAL_STORAGE_KEY = 'pinnedAssetsMapByCustomer';

  const pinnedAssetsMap = useMemo(() => {
    if (selectedCustomerId) {
      return pinnedAssetsMapByCustomer[selectedCustomerId] || {};
    } else {
      const merged: PinnedAssetsMap = {};
      Object.values(pinnedAssetsMapByCustomer).forEach((customerMap) => {
        Object.assign(merged, customerMap);

      });
      return merged;
    }
  }, [selectedCustomerId, pinnedAssetsMapByCustomer]);
  const dispatch = useAppDispatch();
  const plants = useAppSelector((state) => state.summary.plants);
  const assetSummary = useAppSelector((state) => state.summary.assetSummary);
  const totalCount = useAppSelector((state) => state.summary.totalCount);
  const loading = useAppSelector((state) => state.summary.loading);
  const [pinningAssetId, setPinningAssetId] = useState<string | null>(null);
  const prevFilterKeyRef = useRef('');
  const pinnedPageCacheRef = useRef<Record<string, PaginatedAssetSummaryResponse>>({});
  const pinnedInitDoneRef = useRef(false);
  const sitesLiteCacheRef = useRef<Record<string, ExtractedSite[]>>({});
  const plantsBySiteCacheRef = useRef<Record<string, ExtractedPlant[]>>({});
  const customersRef = useRef(customers);
  customersRef.current = customers;
  const extractedSitesRef = useRef(extractedSites);
  extractedSitesRef.current = extractedSites;


// Load sites for a selected customer; otherwise derive from loaded summary rows
useEffect(() => {
  if (!selectedCustomerId) return;

  const cached = sitesLiteCacheRef.current[selectedCustomerId];
  if (cached) {
    setExtractedSites(cached);
    return;
  }

  const loadSitesForCustomer = async () => {
    try {
      const response = await getSitesLite(selectedCustomerId);
      const siteData = (response as { Data?: unknown })?.Data ?? response;
      const siteList = Array.isArray(siteData) ? siteData : [];
      const customer = customersRef.current.find((c) => c.customerId === selectedCustomerId);
      const mapped = siteList.map((site: { siteId: string; site?: string; siteName?: string }) => ({
        siteId: site.siteId,
        site: site.site || site.siteName || '',
        customerId: selectedCustomerId,
        customerName: customer?.customerName || '',
      }));

      sitesLiteCacheRef.current[selectedCustomerId] = mapped;
      setExtractedSites(mapped);
    } catch {
      setExtractedSites([]);
    }
  };

  loadSitesForCustomer();
}, [selectedCustomerId]);

// Backfill customer names on cached sites once the customer list finishes loading
useEffect(() => {
  if (!selectedCustomerId || customers.length === 0) return;

  const customer = customers.find((c) => c.customerId === selectedCustomerId);
  if (!customer?.customerName) return;

  setExtractedSites((prev) => {
    const needsUpdate = prev.some(
      (site) => site.customerId === selectedCustomerId && site.customerName !== customer.customerName
    );
    if (!needsUpdate) return prev;

    const updated = prev.map((site) =>
      site.customerId === selectedCustomerId
        ? { ...site, customerName: customer.customerName }
        : site
    );
    sitesLiteCacheRef.current[selectedCustomerId] = updated;
    return updated;
  });
}, [customers, selectedCustomerId]);

// Merge site/plant options from the currently loaded summary page when no customer is selected
useEffect(() => {
  if (selectedCustomerId || !Array.isArray(assetSummary) || assetSummary.length === 0) return;

  setExtractedSites((prev) => mergeSitesFromAssets(assetSummary, prev));
  setExtractedPlants((prev) => mergePlantsFromAssets(assetSummary, prev));
}, [assetSummary, selectedCustomerId]);

// Derive plant options from summary when a customer is selected but no site is selected
useEffect(() => {
  if (selectedSiteId) return;

  if (selectedCustomerId) {
    setExtractedPlants((prev) =>
      mergePlantsFromAssets(
        assetSummary.filter((asset) => asset.customerId === selectedCustomerId),
        prev
      )
    );
  } else {
    setExtractedPlants([]);
  }
}, [selectedSiteId, selectedCustomerId, assetSummary]);

// Load plants when a site is selected (API call only when siteId changes; cached by siteId)
useEffect(() => {
  if (!selectedSiteId) return;

  const cached = plantsBySiteCacheRef.current[selectedSiteId];
  if (cached) {
    setExtractedPlants(cached);
    return;
  }

  const loadPlantsForSite = async () => {
    try {
      const resp = await getPlantsBySite(selectedSiteId);
      const plantData = resp.data?.Data ?? [];
      const site = extractedSitesRef.current.find((s) => s.siteId === selectedSiteId);
      const customer = customersRef.current.find((c) => c.customerId === selectedCustomerId);
      const siteInfo: ExtractedSite = site || {
        siteId: selectedSiteId,
        site: '',
        customerId: selectedCustomerId,
        customerName: customer?.customerName || '',
      };

      const mapped = (Array.isArray(plantData) ? plantData : []).map(
        (plant: { plantId?: string; id?: string; plantName?: string; name?: string }) => ({
          plantId: plant.plantId || plant.id || '',
          plantName: plant.plantName || plant.name || '',
          siteId: siteInfo.siteId,
          siteName: siteInfo.site,
          customerId: siteInfo.customerId,
          customerName: siteInfo.customerName,
        })
      );

      plantsBySiteCacheRef.current[selectedSiteId] = mapped;
      setExtractedPlants(mapped);
    } catch {
      setExtractedPlants([]);
    }
  };

  loadPlantsForSite();
}, [selectedSiteId, selectedCustomerId]);

  // Refresh token on component mount
  useEffect(() => {
    const initializeTokenRefresh = async () => {
      try {
        const result = await refershTokenApi();
      } catch (error) {
        console.error("Summary: Error during token refresh:", error);
      }
    };

    initializeTokenRefresh();
  }, []);

  useEffect(() => {
    if (!Array.isArray(assetSummary)) {
      setClientFilteredAssets([]);
      return;
    }
    setClientFilteredAssets([...assetSummary]);
  }, [assetSummary]);
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch customers using new GetAccountsLite API endpoint
        const customerAccountsData = await getAccountsLite();
        
        
        // Ensure we have an array to work with
        if (!Array.isArray(customerAccountsData)) {
          setCustomers([]);
          return;
        }
        
        // Transform the API response to match CustomerLite interface
        const validCustomers = customerAccountsData.map((customer: CustomerAccountLite, index: number) => ({
          id: customer.id || `customer-${index}`,
          customerId: customer.customerId,
          customerName: customer.customerName,
          soldTo: customer.salesDistrictCustomer || customer.salesDistrict || '', // Use salesDistrictCustomer as soldTo fallback
          sites: [] // Sites will be fetched separately when customer is selected
        }));
        
        // Remove duplicates based on customerId to prevent React key warnings
        type CustomerItem = { id: string; customerId: string; customerName: string; soldTo?: string };
        const uniqueCustomers = validCustomers.filter((customer: CustomerItem, index: number, self: CustomerItem[]) =>
          index === self.findIndex((c: CustomerItem) => c.customerId === customer.customerId)
        );
        
        setCustomers(uniqueCustomers);
      } catch (error) {
        setCustomers([]);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

 

useEffect(() => {
  if (activeTab !== 'assets') return;

  const filterKey = `${selectedCustomerId}|${selectedSiteId}|${selectedPlantId}`;
  const filtersChanged = prevFilterKeyRef.current !== filterKey && prevFilterKeyRef.current !== '';
  prevFilterKeyRef.current = filterKey;

  const filters = {
    pageNumber: 1,
    pageSize: itemsPerPage,
    ...(selectedCustomerId ? { customerId: selectedCustomerId } : {}),
    ...(selectedSiteId ? { siteId: selectedSiteId } : {}),
    ...(selectedPlantId ? { plantId: selectedPlantId } : {}),
  };

  const cacheKey = buildSummaryCacheKey('assets', filters);

  if (filtersChanged) {
    dispatch(clearAssetSummaryPageCache());
    dispatch(fetchAssetSummary(filters));
    return;
  }

  const cached = store.getState().summary.assetSummaryPageCache[cacheKey];
  if (cached) {
    dispatch(loadAssetSummaryFromCache(cacheKey));
  } else {
    dispatch(fetchAssetSummary(filters));
  }
}, [selectedCustomerId, selectedSiteId, selectedPlantId, activeTab, dispatch]);

const syncPinnedItemsToMap = useCallback((items: AssetSummaryItem[]) => {
  if (items.length === 0) return;

  const existingByCustomer = store.getState().summary.pinnedAssetsMapByCustomer;
  const updatesByCustomer: Record<string, PinnedAssetsMap> = {};

  items.forEach((asset) => {
    const customerId = asset.customerId;
    const assetId = asset.assetId || asset.id;
    if (!customerId || !assetId) return;

    if (!updatesByCustomer[customerId]) {
      updatesByCustomer[customerId] = { ...(existingByCustomer[customerId] || {}) };
    }
    updatesByCustomer[customerId][assetId] = {
      ...asset,
      assetId,
      pinnedAssetId: assetId,
    };
  });

  Object.entries(updatesByCustomer).forEach(([customerId, pinnedAssets]) => {
    dispatch(setPinnedAssetsMapByCustomer({ customerId, pinnedAssets }));
  });
}, [dispatch]);

const syncPinnedFromApi = useCallback(async (
  customerId?: string,
  siteId?: string,
  plantId?: string
) => {
  try {
    let pageNumber = 1;
    let totalPages = 1;
    let totalCount = 0;
    const allPinned: AssetSummaryItem[] = [];

    do {
      const resp = await getPinnedAssetSummary(pageNumber, 500, customerId, siteId, plantId);
      const parsed = parsePaginatedSummaryResponse(resp.data);
      totalCount = parsed.totalCount;
      allPinned.push(...parsed.items);
      totalPages = parsed.totalPages || 1;
      pageNumber += 1;
    } while (pageNumber <= totalPages);

    setPinnedTotalCount(totalCount);
    setPinnedTotalPages(1);
    setPinnedPageItems(allPinned);

    const customerMap: Record<string, PinnedAssetsMap> = {};
    allPinned.forEach((asset) => {
      const cid = asset.customerId;
      const assetId = asset.assetId || asset.id;
      if (!cid || !assetId) return;
      if (!customerMap[cid]) customerMap[cid] = {};
      customerMap[cid][assetId] = { ...asset, assetId, pinnedAssetId: assetId };
    });

    const existingByCustomer = store.getState().summary.pinnedAssetsMapByCustomer;

    if (!customerId) {
      const allCustomerIds = new Set([
        ...Object.keys(existingByCustomer),
        ...Object.keys(customerMap),
      ]);
      allCustomerIds.forEach((cid) => {
        dispatch(setPinnedAssetsMapByCustomer({
          customerId: cid,
          pinnedAssets: customerMap[cid] || {},
        }));
      });
    } else {
      dispatch(setPinnedAssetsMapByCustomer({
        customerId,
        pinnedAssets: customerMap[customerId] || {},
      }));
    }
  } catch {
    // Keep localStorage / existing count on failure
  }
}, [dispatch]);

const fetchPinnedCount = useCallback(async () => {
  try {
    const resp = await getPinnedAssetSummary(
      1,
      1,
      selectedCustomerId || undefined,
      selectedSiteId || undefined,
      selectedPlantId || undefined
    );
    const parsed = parsePaginatedSummaryResponse(resp.data);
    setPinnedTotalCount(parsed.totalCount);
  } catch {
    // Preserve existing count when the count-only request fails
  }
}, [selectedCustomerId, selectedSiteId, selectedPlantId]);

const fetchPinnedPageData = useCallback(async (options?: { forceRefresh?: boolean }) => {
  const cacheKey = buildSummaryCacheKey('pinned', {
    pageNumber: 1,
    pageSize: itemsPerPage,
    customerId: selectedCustomerId || undefined,
    siteId: selectedSiteId || undefined,
    plantId: selectedPlantId || undefined,
  });

  if (options?.forceRefresh) {
    delete pinnedPageCacheRef.current[cacheKey];
  } else {
    const cached = pinnedPageCacheRef.current[cacheKey];
    if (cached) {
      setPinnedPageItems(cached.items);
      setPinnedTotalCount(cached.totalCount);
      setPinnedTotalPages(cached.totalPages);
      return;
    }
  }

  setPinnedPageLoading(true);
  try {
    const fetchPageSize = 500;
    let pageNumber = 1;
    let totalPages = 1;
    let totalCount = 0;
    const allItems: AssetSummaryItem[] = [];

    do {
      const resp = await getPinnedAssetSummary(
        pageNumber,
        fetchPageSize,
        selectedCustomerId || undefined,
        selectedSiteId || undefined,
        selectedPlantId || undefined
      );
      const parsed = parsePaginatedSummaryResponse(resp.data);
      allItems.push(...parsed.items);
      totalCount = parsed.totalCount;
      totalPages = parsed.totalPages || 1;
      pageNumber += 1;
    } while (pageNumber <= totalPages);

    const aggregated: PaginatedAssetSummaryResponse = {
      items: allItems,
      totalCount,
      pageNumber: 1,
      pageSize: allItems.length,
      totalPages: 1,
    };

    pinnedPageCacheRef.current[cacheKey] = aggregated;
    setPinnedPageItems(aggregated.items);
    setPinnedTotalCount(aggregated.totalCount);
    setPinnedTotalPages(aggregated.totalPages);
    syncPinnedItemsToMap(aggregated.items);
  } catch {
    setPinnedPageItems([]);
    setPinnedTotalPages(0);
  } finally {
    setPinnedPageLoading(false);
  }
}, [itemsPerPage, selectedCustomerId, selectedSiteId, selectedPlantId, syncPinnedItemsToMap]);

const pinnedFilterKeyRef = useRef('');
const prevActiveTabRef = useRef(activeTab);
const prevPinnedFiltersForCountRef = useRef('');

// Initialize pinned state: localStorage first, then API after token refresh
useEffect(() => {
  if (!isLoggedIn) return;

  let cancelled = false;
  pinnedInitDoneRef.current = false;
  setPinnedInitDone(false);

  const initializePinned = async () => {
    dispatch(loadPinnedAssetsFromStorage());

    const storedPinnedAssets = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');
    const interimCount = countPinnedAssetsInMap(storedPinnedAssets);
    if (interimCount > 0) {
      setPinnedTotalCount(interimCount);
    }

    try {
      await refershTokenApi();
    } catch (error) {
      console.error('Summary: Error during token refresh:', error);
    }

    if (cancelled) return;

    await syncPinnedFromApi();
    pinnedInitDoneRef.current = true;
    setPinnedInitDone(true);
    prevPinnedFiltersForCountRef.current = `${selectedCustomerId}|${selectedSiteId}|${selectedPlantId}`;
  };

  initializePinned();

  return () => {
    cancelled = true;
  };
}, [isLoggedIn, dispatch, syncPinnedFromApi]);

// Update pinned badge count when filters change (after initial sync)
useEffect(() => {
  if (!isLoggedIn || activeTab === 'pinned' || !pinnedInitDone) return;

  const filterKey = `${selectedCustomerId}|${selectedSiteId}|${selectedPlantId}`;
  if (prevPinnedFiltersForCountRef.current === filterKey) return;
  prevPinnedFiltersForCountRef.current = filterKey;

  fetchPinnedCount();
}, [isLoggedIn, activeTab, pinnedInitDone, fetchPinnedCount, selectedCustomerId, selectedSiteId, selectedPlantId]);

// Full pinned table data when pinned tab is active (pageSize 10 — items + totalCount)
useEffect(() => {
  if (!isLoggedIn || activeTab !== 'pinned' || !pinnedInitDone) {
    if (activeTab !== 'pinned') prevActiveTabRef.current = activeTab;
    return;
  }

  const justOpenedPinnedTab = prevActiveTabRef.current !== 'pinned';
  prevActiveTabRef.current = activeTab;

  const filterKey = `${selectedCustomerId}|${selectedSiteId}|${selectedPlantId}`;
  const filtersChanged = pinnedFilterKeyRef.current !== filterKey && pinnedFilterKeyRef.current !== '';
  pinnedFilterKeyRef.current = filterKey;

  if (filtersChanged || justOpenedPinnedTab) {
    pinnedPageCacheRef.current = {};
  }
  fetchPinnedPageData({ forceRefresh: justOpenedPinnedTab || filtersChanged });
}, [isLoggedIn, activeTab, pinnedInitDone, fetchPinnedPageData, selectedCustomerId, selectedSiteId, selectedPlantId]);

  // Fetch asset types on mount
  useEffect(() => {
    const fetchAssetTypes = async () => {
      try {
        const response = await getAssetTypes();
        // Adjust if your API returns { Data: [...] }
        setAssetTypes(response.Data || []);
      } catch (err) {
        setAssetTypes([]);
      }
    };
    fetchAssetTypes();
  }, []);

  // Handler for customer dropdown change
  const handleCustomerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCustomerId(e.target.value);
    setSelectedSiteId('');
    setSelectedPlantId('');
    setSelectedAssetType('');
    setCurrentPage(1); // Reset pagination when filter changes
  };

// Handler for site dropdown change - with auto customer selection
const handleSiteChange = (siteId: string) => {
  setSelectedSiteId(siteId);
  setSelectedPlantId(''); // Reset plant when site changes
  
  // AUTO-SELECT CUSTOMER: If a site is selected, find and select its customer
  if (siteId) {
    const selectedSite = extractedSites.find(s => s.siteId === siteId);
    if (selectedSite) {
      // Only update if not already selected or different
      if (!selectedCustomerId || selectedCustomerId !== selectedSite.customerId) {
        setSelectedCustomerId(selectedSite.customerId);
      }
    }
  }
  
  setSelectedAssetType('');
  setCurrentPage(1);
};

// Handler for plant dropdown change
const handlePlantChange = (plantId: string) => {
  setSelectedPlantId(plantId);
  
  // If a plant is selected, find its customer and site
  if (plantId) {
    const selectedPlant = extractedPlants.find(p => p.plantId === plantId);
    if (selectedPlant) {
      // Auto-select the customer for this plant
      if (!selectedCustomerId || selectedCustomerId !== selectedPlant.customerId) {
        setSelectedCustomerId(selectedPlant.customerId);
      }
      
      // Auto-select the site for this plant
      if (!selectedSiteId || selectedSiteId !== selectedPlant.siteId) {
        setSelectedSiteId(selectedPlant.siteId);
      }
    }
  }
  
  setSelectedAssetType('');
  setCurrentPage(1);
};


  // Handler for asset type dropdown change
  const handleAssetTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAssetType(e.target.value);
    setCurrentPage(1); // Reset pagination when filter changes
  };

  // Handler for alerts toggle change
  const handleAlertsToggleChange = () => {
    setShowAlertsOnly(v => !v);
    setCurrentPage(1); // Reset pagination when filter changes
  };

  // Filtered lists for searchable dropdowns
  const filteredCustomersList = React.useMemo(() => {
    const list = customers || [];
    const normalize = (s: any) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
    const term = normalize(customerSearch);
    if (!term) return list;
    return list.filter(c => {
      const name = normalize(c.customerName);
      const soldTo = normalize(c.soldTo);
      // Perfect match first, then partial
      if (name === term || soldTo === term) return true;
      return name.includes(term) || soldTo.includes(term);
    });
  }, [customers, customerSearch]);

const filteredSitesList = React.useMemo(() => {
  let sitesToShow = extractedSites;
  
  // Filter by customer if selected
  if (selectedCustomerId) {
    sitesToShow = extractedSites.filter(site => site.customerId === selectedCustomerId);
  }
  
  // Apply search filter
  const normalize = (text: any) => String(text || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const term = normalize(siteSearch);
  
  if (!term) return sitesToShow;
  
  return sitesToShow.filter(site => {
    const siteName = normalize(site.site);
    const siteId = normalize(site.siteId);
    const customerName = normalize(site.customerName);
    
    if (siteName === term || siteId === term || customerName === term) return true;
    return siteName.includes(term) || siteId.includes(term) || customerName.includes(term);
  });
}, [extractedSites, selectedCustomerId, siteSearch]);
const filteredPlantsList = React.useMemo(() => {
  let plantsToShow = extractedPlants;
  
  // Filter by site if selected
  if (selectedSiteId) {
    plantsToShow = extractedPlants.filter(plant => plant.siteId === selectedSiteId);
  }
  // Also filter by customer if selected (when no site selected)
  else if (selectedCustomerId) {
    plantsToShow = extractedPlants.filter(plant => plant.customerId === selectedCustomerId);
  }
  
  // Apply search filter
  const normalize = (text: any) => String(text || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const term = normalize(plantSearch);
  
  if (!term) return plantsToShow;
  
  return plantsToShow.filter(plant => {
    const plantName = normalize(plant.plantName);
    const plantId = normalize(plant.plantId);
    const siteName = normalize(plant.siteName);
    
    if (plantName === term || plantId === term) return true;
    return plantName.includes(term) || plantId.includes(term) || siteName.includes(term);
  });
}, [extractedPlants, selectedSiteId, selectedCustomerId, plantSearch]);

  const filteredAssetTypesList = React.useMemo(() => {
    const list = assetTypes || [];
    if (!assetTypeSearch.trim()) return list;
    const term = assetTypeSearch.toLowerCase();
    return list.filter((t: any) => t.typeName && String(t.typeName).toLowerCase().includes(term));
  }, [assetTypes, assetTypeSearch]);

  // Close any open dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      const inDropdown = target.closest('.searchable-select-container-summary');
      if (!inDropdown) {
        setIsCustomerOpen(false);
        setIsSiteOpen(false);
        setIsPlantOpen(false);
        setIsAssetTypeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handler for alerts modal
  const handleAlertsClick = (asset: any) => {
    // Use the alerts data from the asset object
    const alertsData = asset.alerts || [];

    setSelectedAssetAlerts({
      assetName: asset.assetName || 'Unknown Asset',
      alerts: alertsData as AlertItem[]
    });
    setShowAlertsModal(true);
  };

  const closeAlertsModal = () => {
    setShowAlertsModal(false);
    setSelectedAssetAlerts(null);
  };

  const handleGatewayStatusClick = async (asset: { assetName?: string; gatewayDeviceId?: string }) => {
    const assetName = asset.assetName || 'Unknown Asset';

    setSelectedGatewayStatus({
      assetName,
      status: '',
      loading: true,
      error: null,
    });
    setShowGatewayStatusModal(true);

    if (!asset.gatewayDeviceId) {
      setSelectedGatewayStatus({
        assetName,
        status: '',
        loading: false,
        error: 'No gateway device configured for this asset.',
      });
      return;
    }

    const status = await getGatewayStatus(asset.gatewayDeviceId);
    setSelectedGatewayStatus({
      assetName,
      status,
      loading: false,
      error: status ? null : 'Unable to retrieve gateway status.',
    });
  };

  const closeGatewayStatusModal = () => {
    setShowGatewayStatusModal(false);
    setSelectedGatewayStatus(null);
  };

  type AssetRow = Record<string, any> & { alerts?: string[] };

  // Table rows from API page data; fall back to pin map only when page items are empty
  const pinnedRows = React.useMemo(() => {
    const source =
      pinnedPageItems.length > 0
        ? pinnedPageItems
        : activeTab === 'pinned'
          ? Object.values(pinnedAssetsMap)
          : [];

    return source.map((row: any) => ({
      ...row,
      assetId: row.assetId || row.id,
      isPinned: true,
      alerts: row.alerts || [],
    }));
  }, [pinnedPageItems, pinnedAssetsMap, activeTab]);

  // First, filter by asset type

  // Update all places that use assetSummary to use clientFilteredAssets instead
  const assetSummaryWithAlerts = React.useMemo(() => {
    function isPlainObject(value: unknown): value is AssetRow {
      return typeof value === 'object' && value !== null && !Array.isArray(value);
    }
    
    // Use clientFilteredAssets instead of assetSummary
    if (!Array.isArray(clientFilteredAssets) || clientFilteredAssets.length === 0) return [];

    return clientFilteredAssets.map((row, idx) => {
      if (isPlainObject(row)) {
        const typedRow = row as AssetRow;
        const assetId = row.assetId as string;
        const isPinned = Boolean(pinnedAssetsMap[assetId]);

        return {
          ...typedRow,
          isPinned,
          alerts: row.alerts || [],
        };
      }
      return row;
    });
  }, [clientFilteredAssets, pinnedAssetsMap]);
  const assetTypeFiltered = React.useMemo(() => {
    if (!selectedAssetType) return assetSummaryWithAlerts;
    const selectedType = String(selectedAssetType).toLowerCase();
    return assetSummaryWithAlerts.filter((row: any) => {
      return (
        (row.assetTypeName && String(row.assetTypeName).toLowerCase() === selectedType) ||
        (row.assetType && String(row.assetType).toLowerCase() === selectedType)
      );
    });
  }, [assetSummaryWithAlerts, selectedAssetType]);

  // Filter by alerts if toggle is on - memoized to prevent infinite loops
  const alertsFiltered = React.useMemo(() => {
    if (!showAlertsOnly) return assetTypeFiltered;
    return assetTypeFiltered.filter((row: any) => {
      const alertsCount = parseInt(row.alertsCount) || 0;
      const hasAlerts = Array.isArray(row.alerts) && row.alerts.length > 0;
      const hasAlertsCount = alertsCount > 0;
      return hasAlerts || hasAlertsCount;
    });
  }, [assetTypeFiltered, showAlertsOnly]);

  // Then, use the search hook on the filtered data
  const { searchText, filteredData: filteredAssetSummary, handleSearchChange } = useSearch(alertsFiltered);

  // Apply dropdown filters to pinned rows
  const dropdownFilteredPinnedRows = React.useMemo(() => {
    let filtered = pinnedRows;

    // Filter by customer if selected
    if (selectedCustomerId) {
      filtered = filtered.filter((row: any) => row.customerId === selectedCustomerId);
    }

    // Filter by site if selected
    if (selectedSiteId) {
      filtered = filtered.filter((row: any) => row.siteId === selectedSiteId);
    }

    // Filter by plant if selected
    if (selectedPlantId) {
      filtered = filtered.filter((row: any) => row.plantId === selectedPlantId);
    }

    // Filter by asset type if selected
    if (selectedAssetType) {
      const selectedType = String(selectedAssetType).toLowerCase();
      filtered = filtered.filter((row: any) => {
        return (
          (row.assetTypeName && String(row.assetTypeName).toLowerCase() === selectedType) ||
          (row.assetType && String(row.assetType).toLowerCase() === selectedType)
        );
      });
    }

    // Filter by alerts if toggle is on
    if (showAlertsOnly) {
      filtered = filtered.filter((row: any) => {
        // Check if there are alerts in the alerts array OR if alertsCount is greater than 0
        const hasAlertsArray = Array.isArray(row.alerts) && row.alerts.length > 0;
        const hasAlertsCount = row.alertsCount && (parseInt(row.alertsCount) > 0 || row.alertsCount !== '0');
        return hasAlertsArray || hasAlertsCount;
      });
    }

    return filtered;
  }, [pinnedRows, selectedCustomerId, selectedSiteId, selectedPlantId, selectedAssetType, showAlertsOnly]);

  // Apply search filtering to pinned rows
  const filteredPinnedRows = React.useMemo(() => {
    if (!searchText || !searchText.trim()) {
      return dropdownFilteredPinnedRows;
    }

    const searchLower = searchText.toLowerCase();
    return dropdownFilteredPinnedRows.filter((row: any) => {
      return (
        (row.customerName && row.customerName.toLowerCase().includes(searchLower)) ||
        (row.siteName && row.siteName.toLowerCase().includes(searchLower)) ||
        (row.plantName && row.plantName.toLowerCase().includes(searchLower)) ||
        (row.assetType && row.assetType.toLowerCase().includes(searchLower)) ||
        (row.assetName && row.assetName.toLowerCase().includes(searchLower)) ||
        (row.status && row.status.toLowerCase().includes(searchLower))
      );
    });
  }, [dropdownFilteredPinnedRows, searchText]);

  // Use your existing SVG icons for sorting
const SortIcon = ({ columnKey, className = "" }: { columnKey: SortableKeys; className?: string }) => {
  // Determine which sort config to use based on active tab
  const isActive = activeTab === 'assets' 
    ? (sortConfig && sortConfig.key === columnKey)
    : (pinnedSortConfig && pinnedSortConfig.key === columnKey);
  
  const direction = isActive 
    ? (activeTab === 'assets' ? sortConfig?.direction : pinnedSortConfig?.direction)
    : null;

  return (
    <div
      className={`table-sort-icon ${direction ? `sort-${direction}` : ''} ${className}`}
      tabIndex={0}
      role="button"
      aria-label={`Sort by ${columnKey}`}
      onClick={(e) => {
        e.stopPropagation();
        requestSort(columnKey);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          requestSort(columnKey);
        }
      }}
    >
      {isActive ? (
        direction === 'asc' ? (
          <img src={ArrowUp} alt="Sort ascending" className="sort-icon-img-up" />
        ) : (
          <img src={ArrowDown} alt="Sort descending" className="sort-icon-img-down" />
        )
      ) : (
        <img src={ArrowDefault} alt="Sort" className="sort-icon-img" />
      )}
    </div>
  );
};

  const getSortArrow = (columnKey: SortableKeys) => {
    return <SortIcon columnKey={columnKey} />;
  };
  // Add this with your other state declarations
const [pinnedSortConfig, setPinnedSortConfig] = useState<{ 
  key: SortableKeys; 
  direction: 'asc' | 'desc' 
} | null>(null);

  const requestSort = (key: SortableKeys) => {
  if (activeTab === 'pinned') {
    // Handle sorting for pinned tab
    let direction: 'asc' | 'desc' = 'asc';
    if (pinnedSortConfig?.key === key && pinnedSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setPinnedSortConfig({ key, direction });
    setSortConfig(null); // Clear assets sort config
  } else {
    // Handle sorting for assets tab (existing logic)
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPinnedSortConfig(null); // Clear pinned sort config
  }
};

  const sortedAssetSummary = React.useMemo(() => {
    const sortableItems = [...filteredAssetSummary];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        // Map the sort keys to the actual data properties
        switch (sortConfig.key) {
          case 'customer':
            aValue = a.customerName;
            bValue = b.customerName;
            break;
          case 'site':
            aValue = a.siteName;
            bValue = b.siteName;
            break;
          case 'plant':
            aValue = a.plantName;
            bValue = b.plantName;
            break;
          case 'assetType':
            aValue = a.assetType;
            bValue = b.assetType;
            break;
          case 'assetName':
            aValue = a.assetName;
            bValue = b.assetName;
            break;
          case 'status':
            aValue = a.status;
            bValue = b.status;
            break;
          case 'alertsCount':
            aValue = parseInt(a.alertsCount) || 0;
            bValue = parseInt(b.alertsCount) || 0;
            break;
          case 'lastCommunication':
            aValue = a.lastCommunication;
            bValue = b.lastCommunication;
            break;
          case 'firstCommunication':
            aValue = a.firstCommunication;
            bValue = b.firstCommunication;
            break; 
          default:
            aValue = '';
            bValue = '';
        }

        if (aValue === undefined || aValue === null) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        if (bValue === undefined || bValue === null) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }

        // Handle numeric comparison for alertsCount
        if (sortConfig.key === 'alertsCount') {
          if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        }

        const valA = String(aValue).toLowerCase();
        const valB = String(bValue).toLowerCase();

        if (valA < valB) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (valA > valB) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredAssetSummary, sortConfig]);

  const sortedPinnedRows = React.useMemo(() => {
  const sortableItems = [...filteredPinnedRows];
  
  if (pinnedSortConfig !== null) {
    sortableItems.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Map the sort keys to the actual data properties
      switch (pinnedSortConfig.key) {
        case 'customer':
          aValue = a.customerName;
          bValue = b.customerName;
          break;
        case 'site':
          aValue = a.siteName;
          bValue = b.siteName;
          break;
        case 'plant':
          aValue = a.plantName;
          bValue = b.plantName;
          break;
        case 'assetType':
          aValue = a.assetType;
          bValue = b.assetType;
          break;
        case 'assetName':
          aValue = a.assetName;
          bValue = b.assetName;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'alertsCount':
          aValue = parseInt(a.alertsCount) || 0;
          bValue = parseInt(b.alertsCount) || 0;
          break;
        case 'lastCommunication':
          aValue = a.lastCommunication;
          bValue = b.lastCommunication;
          break;
        case 'firstCommunication':
          aValue = a.firstCommunication;
          bValue = b.firstCommunication;
          break; 
        default:
          aValue = '';
          bValue = '';
      }

      if (aValue === undefined || aValue === null) {
        return pinnedSortConfig.direction === 'asc' ? 1 : -1;
      }
      if (bValue === undefined || bValue === null) {
        return pinnedSortConfig.direction === 'asc' ? -1 : 1;
      }

      // Handle numeric comparison for alertsCount
      if (pinnedSortConfig.key === 'alertsCount') {
        if (aValue < bValue) {
          return pinnedSortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return pinnedSortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      }

      const valA = String(aValue).toLowerCase();
      const valB = String(bValue).toLowerCase();

      if (valA < valB) {
        return pinnedSortConfig.direction === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return pinnedSortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }
  return sortableItems;
}, [filteredPinnedRows, pinnedSortConfig]);


  const totalItems = React.useMemo(() => {
    if (activeTab === 'mapview') return 0;
    if (activeTab === 'assets') return filteredAssetSummary.length;
    return filteredPinnedRows.length;
  }, [activeTab, filteredAssetSummary.length, filteredPinnedRows.length]);

  const totalPages = React.useMemo(() => {
    if (activeTab === 'mapview') return 0;
    return Math.max(1, Math.ceil(totalItems / itemsPerPage));
  }, [activeTab, totalItems, itemsPerPage]);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToNextPage = () => {
    if (currentPage < totalPages && totalPages > 0) {
      setCurrentPage(prev => Math.min(prev + 1, totalPages));
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => Math.max(prev - 1, 1));
    }
  };

  // Slice the fully filtered + sorted data set into the current page.
  const pagedAssetSummary = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedAssetSummary.slice(start, start + itemsPerPage);
  }, [sortedAssetSummary, currentPage, itemsPerPage]);

  const pagedPinnedRows = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedPinnedRows.slice(start, start + itemsPerPage);
  }, [sortedPinnedRows, currentPage, itemsPerPage]);

  // Keep the current page in range whenever filtering/searching shrinks the result set.
  useEffect(() => {
    if (activeTab === 'mapview') return;
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [activeTab, currentPage, totalPages]);

  const handlePinClick = async (asset: any) => {
    const assetId = asset.assetId;

    //  Determine the actual customer ID for this asset
    const customerIdToUpdate = selectedCustomerId || asset.customerId;

    if (!customerIdToUpdate) {
      return;
    }

    //  Get the correct pinned map for that customer
    const currentCustomerMap = pinnedAssetsMapByCustomer[customerIdToUpdate] || {};
    const existingPin = currentCustomerMap[assetId];
    const isOnPinnedTab = activeTab === 'pinned';
    const isPinnedInMap = Boolean(existingPin || pinnedAssetsMap[assetId]);
    const shouldUnpin = isOnPinnedTab || isPinnedInMap;

    setPinningAssetId(assetId);

    try {
      // ✅ Get current user ID for the pin payload
      const currentUserId = getCurrentUserId();

      // ✅ Use the Redux thunk with simplified PinPayload structure
      const result = await dispatch(pinAsset({
        assetId: assetId,
        isPinned: !shouldUnpin
      })).unwrap();

      // ✅ Update local state based on the result
      const updatedMap = { ...currentCustomerMap };

      if (shouldUnpin) {
        delete updatedMap[assetId];
        toast.success('Unpinned Successfully');
        setPinnedPageItems((prev) => prev.filter((item) => (item.assetId || item.id) !== assetId));
      } else {
        updatedMap[assetId] = {
          ...asset,
          assetId,
          pinnedAssetId: assetId,
        };
        toast.success('Pinned Successfully');
      }

      //  Update Redux store (localStorage persistence is handled in the action)
      dispatch(setPinnedAssetsMapByCustomer({
        customerId: customerIdToUpdate,
        pinnedAssets: updatedMap,
      }));

      pinnedPageCacheRef.current = {};
      await syncPinnedFromApi(
        selectedCustomerId || undefined,
        selectedSiteId || undefined,
        selectedPlantId || undefined
      );

    } catch (error) {
      toast.error('Failed to update pin status');
    } finally {
      setPinningAssetId(null);
    }
  };




  const handleAssetNameClick = (asset: any) => {
    const params = new URLSearchParams();
    if (asset.customerId) params.append('customerId', asset.customerId);
    if (asset.siteId) params.append('siteId', asset.siteId);
    if (asset.plantId) params.append('plantId', asset.plantId);
    if (asset.assetId) params.append('selectedAssetId', asset.assetId);

    const queryString = params.toString();
    const url = queryString ? `/asset-details/${asset.assetId}?${queryString}` : `/asset-details/${asset.assetId}`;
    navigate(url, {
      state: {
        assetName: asset.assetName,
        breadcrumbTitle: asset.assetName
      }
    });
  };
  // Determine if button should be disabled
// Only disable if a customer is selected AND there are no assets
const hasAssetsToDownload = activeTab === 'assets'
  ? totalCount > 0
  : pinnedTotalCount > 0;


  // Download assets as CSV function
  const handleTabChange = (tab: 'assets' | 'pinned' | 'mapview') => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset pagination when switching tabs
  };
 

  //         'Accept': 'text/csv'
  //     link.href = url;

  //       filename += `_${customer?.customerName || selectedCustomerId}`;
  //       filename += `_${site?.site || selectedSiteId}`;
  //       filename += `_${plant?.plantName || selectedPlantId}`;
  //     filename += '.csv';

  //     link.download = filename;
  //     document.body.appendChild(link);
  //     link.click();

  //     document.body.removeChild(link);
  //     window.URL.revokeObjectURL(url);

  // Display logic based on active tab
  const handleDownloadCSV = async () => {
  try {
    // First check: No customer selected
    if (!selectedCustomerId) {
      toast.warning('Please select a customer to download assets.');
      return;
    }
    
    // Second check: No assets available based on active tab
    let hasAssets = false;
    
    if (activeTab === 'assets') {
      hasAssets = totalCount > 0;
      if (!hasAssets) {
        toast.warning('No assets available to download for the selected filters.');
        return;
      }
    } else if (activeTab === 'pinned') {
      hasAssets = pinnedTotalCount > 0;
      if (!hasAssets) {
        toast.warning('No pinned assets available to download.');
        return;
      }
    } else if (activeTab === 'mapview') {
      toast.warning('Download is not available in Map View.');
      return;
    }

    // Build the URL with proper parameter handling
    let apiUrl = '';

    if (selectedCustomerId && selectedSiteId && selectedPlantId) {
      // All three parameters
      apiUrl = `/Asset/Download/${selectedCustomerId}/${selectedSiteId}/${selectedPlantId}`;
    } else if (selectedCustomerId && selectedSiteId) {
      // Customer and site only
      apiUrl = `/Asset/Download/${selectedCustomerId}/${selectedSiteId}`;
    } else if (selectedCustomerId) {
      // Customer only
      apiUrl = `/Asset/Download/${selectedCustomerId}`;
    }

    // Show loading indicator
    toast.info('Downloading CSV file...', { autoClose: 2000 });

    // Make the API call to download CSV
    const response = await apiInstance.get(apiUrl, {
      responseType: 'blob', // Important for file downloads
      headers: {
        'Accept': 'text/csv'
      }
    });

    // Check if valid data
    if (response.data.size === 0) {
      toast.error('No assets data available to download.');
      return;
    }

    // Create a blob from the response
    const blob = new Blob([response.data], { type: 'text/csv' });

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = url;

    // Generate filename based on current filters
    let filename = 'assets';
    if (selectedCustomerId) {
      const customer = customers.find(c => c.customerId === selectedCustomerId);
      filename += `_${customer?.customerName || selectedCustomerId}`;
    }
    if (selectedSiteId) {
      const site = sites.find(s => s.siteId === selectedSiteId);
      filename += `_${site?.site || selectedSiteId}`;
    }
    if (selectedPlantId && Array.isArray(plants)) {
      const plant = plants.find((p: any) => p.plantId === selectedPlantId) as any;
      filename += `_${plant?.plantName || selectedPlantId}`;
    }
    filename += '.csv';

    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Show success message
    toast.success('CSV file downloaded successfully!');

  } catch (error: any) {
    // Provide user-friendly error messages
    if (error.response?.status === 404) {
      toast.error('Download file not found. Please contact support.');
    } else if (error.response?.status === 400) {
      toast.error('Invalid request parameters. Please check your selection and try again.');
    } else if (error.response?.status === 401) {
      toast.error('You are not authorized to download this data. Please check your permissions.');
    } else if (error.response?.status === 500) {
      toast.error('Server error occurred while generating the CSV. Please try again later.');
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please try again.');
    } else if (error.message === 'Network Error') {
      toast.error('Network error. Please check your connection and try again.');
    } else {
      toast.error('An error occurred while downloading the CSV file. Please try again.');
    }
  }
};

// Determine if button should be disabled
const isDownloadDisabled = Boolean(selectedCustomerId) &&
  ((activeTab === 'assets' && totalCount === 0) ||
   (activeTab === 'pinned' && pinnedTotalCount === 0));
  const displayedAssets = React.useMemo(() => {
    if (activeTab === 'mapview') {
      return []; // No table data for map view
    } else if (activeTab === 'pinned') {
      return pagedPinnedRows;
    } else {
      return pagedAssetSummary;
    }
  }, [activeTab, pagedPinnedRows, pagedAssetSummary]);

  // Count logic for tabs
  const assetsCount = React.useMemo(() => {
    return totalCount;
  }, [totalCount]);

  const pinnedAssetsCount = React.useMemo(() => {
    const mapCount = countPinnedAssetsInMap(
      pinnedAssetsMapByCustomer,
      selectedCustomerId || undefined
    );
    return Math.max(pinnedTotalCount, mapCount);
  }, [pinnedTotalCount, pinnedAssetsMapByCustomer, selectedCustomerId]);

  // localStorage persistence is now handled in the Redux action setPinnedAssetsMapByCustomer

  // Handle ESC key and outside click for modals
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (showAlertsModal) closeAlertsModal();
      if (showGatewayStatusModal) closeGatewayStatusModal();
    };

    if (showAlertsModal || showGatewayStatusModal) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset'; // Restore scroll
    };
  }, [showAlertsModal, showGatewayStatusModal]);

  const options = [
    { value: "", label: "All Customers" },
    ...customers.map((c) => ({
      value: c.customerId,
      label: `${c.customerName} (${c.soldTo})`,
    })),
  ];

  const selectedOption = options.find(o => o.value === selectedCustomerId) || options[0];


  return (
    <div className="summary-container home">
      <div className="summary-page-content">
      {/* Top filter bar - move above sidebar/home */}
      <div className="summary-filters-container new-summary-filter">
        <div className='new-container'>
          {/* Customer searchable dropdown */}
          <div className="searchable-select-container-summary" >
            <div className="searchable-select-input-summary" style={{backgroundColor:'grey/500'}} onClick={() => { const next = !isCustomerOpen; setIsCustomerOpen(next); setIsSiteOpen(false); setIsPlantOpen(false); setIsAssetTypeOpen(false); }}>
              <input
                type="text"
                value={isCustomerOpen ? customerSearch : (selectedCustomerId ? (customers.find(c => c.customerId === selectedCustomerId)?.customerName || '') : '')}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onFocus={() => { /* no-op to avoid first-click open/close race */ }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    setIsCustomerOpen(true);
                  }
                }}
                placeholder={isCustomerOpen ? 'Search customers...' : (selectedCustomerId ? (customers.find(c => c.customerId === selectedCustomerId)?.customerName || '') : 'All Customers')}
                className=" summary-top-input"
                readOnly={!isCustomerOpen}
              />
              <span className={`dropdown-arrow-topinput ${isCustomerOpen ? 'open' : ''}`}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                           </svg></span>
            </div>
            {isCustomerOpen && (
              <div className="searchable-dropdown">
                <div
                  className={`dropdown-option ${selectedCustomerId === '' ? 'selected' : ''}`}
                  onClick={() => { setSelectedCustomerId(''); setSelectedSiteId(''); setSelectedPlantId(''); setSelectedAssetType(''); setIsCustomerOpen(false); setCustomerSearch(''); setCurrentPage(1); }}
                >
                  All Customers
                </div>
                {filteredCustomersList.map((customer, index) => {
                  const label = `${customer.customerName}${customer.soldTo ? ` (${customer.soldTo})` : ''}`;
                  return (
                    <div
                      key={customer.id || `${customer.customerId}-${index}`}
                      className={`dropdown-option ${customer.customerId === selectedCustomerId ? 'selected' : ''}`}
                      onClick={() => { setSelectedCustomerId(customer.customerId); setSelectedSiteId(''); setSelectedPlantId(''); setSelectedAssetType(''); setIsCustomerOpen(false); setCustomerSearch(''); setCurrentPage(1); }}
                    >
                {label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <span className='summary-separator-summary'>\</span>

          {/* Site searchable dropdown */}
{/* Site searchable dropdown */}
<div className="searchable-select-container-summary">
  <div className="searchable-select-input-summary" onClick={() => { const next = !isSiteOpen; setIsSiteOpen(next); setIsCustomerOpen(false); setIsPlantOpen(false); setIsAssetTypeOpen(false); }}>
    <input
      type="text"
      value={isSiteOpen ? siteSearch : 
        (selectedSiteId ? (extractedSites.find(s => s.siteId === selectedSiteId)?.site || '') : '')}
      onChange={(e) => setSiteSearch(e.target.value)}
      onFocus={() => { /* no-op */ }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          setIsSiteOpen(true);
        }
      }}
      placeholder={isSiteOpen ? 'Search sites...' : 
        (selectedSiteId ? (extractedSites.find(s => s.siteId === selectedSiteId)?.site || '') : 'All Sites')}
      className="summary-top-input"
      readOnly={!isSiteOpen}
    />
    <span className={`dropdown-arrow-topinput ${isSiteOpen ? 'open' : ''}`}>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  </div>
  
  {isSiteOpen && (
    <div className="searchable-dropdown">
      <div
        className={`dropdown-option ${selectedSiteId === '' ? 'selected' : ''}`}
        onClick={() => { 
          setSelectedSiteId(''); 
          setSelectedPlantId(''); 
          setIsSiteOpen(false); 
          setSiteSearch(''); 
          setCurrentPage(1); 
        }}
      >
        All Sites
      </div>
      {filteredSitesList.map((site, index) => (
        <div
          key={`${site.siteId}-${index}`}
          className={`dropdown-option ${site.siteId === selectedSiteId ? 'selected' : ''}`}
          onClick={() => { 
            handleSiteChange(site.siteId); // THIS WILL AUTO-SELECT CUSTOMER
            setIsSiteOpen(false); 
            setSiteSearch(''); 
            setCurrentPage(1); 
          }}
          title={`${site.site} `}
        >
          <div className="site-option-content">
            <span className="site-name">{site.site}</span>
            
          </div>
        </div>
      ))}
      
      {filteredSitesList.length === 0 && (
        <div className="dropdown-option disabled">
          No sites found
        </div>
      )}
    </div>
  )}
</div>
          <span className='summary-separator-summary'>\</span>

          
{/* Plant searchable dropdown - CORRECTED VERSION */}
<div className="searchable-select-container-summary">
  <div className="searchable-select-input-summary" onClick={() => { const next = !isPlantOpen; setIsPlantOpen(next); setIsCustomerOpen(false); setIsSiteOpen(false); setIsAssetTypeOpen(false); }}>
    <input
      type="text"
      value={isPlantOpen ? plantSearch : 
        (selectedPlantId ? (extractedPlants.find(p => p.plantId === selectedPlantId)?.plantName || '') : '')}
      onChange={(e) => setPlantSearch(e.target.value)}
      onFocus={() => { /* no-op */ }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          setIsPlantOpen(true);
        }
      }}
      placeholder={isPlantOpen ? 'Search Plants...' : 
        (selectedPlantId ? (extractedPlants.find(p => p.plantId === selectedPlantId)?.plantName || '') : 'All Plants')}
      className="summary-top-input"
      readOnly={!isPlantOpen}
    />
    <span className={`dropdown-arrow-topinput ${isPlantOpen ? 'open' : ''}`}>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  </div>
  
  {isPlantOpen && (
    <div className="searchable-dropdown">
      <div
        className={`dropdown-option ${selectedPlantId === '' ? 'selected' : ''}`}
        onClick={() => { 
          setSelectedPlantId(''); 
          setIsPlantOpen(false); 
          setPlantSearch(''); 
          setCurrentPage(1); 
        }}
      >
        All Plants
      </div>
      
      {/* FIXED: Use filteredPlantsList.map with proper parameters */}
      {filteredPlantsList.map((plant, index) => (
        <div
          key={`${plant.plantId}-${index}`}
          className={`dropdown-option ${plant.plantId === selectedPlantId ? 'selected' : ''}`}
          onClick={() => { 
            handlePlantChange(plant.plantId); // Use the new handler
            setIsPlantOpen(false); 
            setPlantSearch(''); 
            setCurrentPage(1); 
          }}
          title={`${plant.plantName} `}
        >
          <div className="plant-option-content">
            <span className="plant-name">{plant.plantName}</span>
            
          </div>
        </div>
      ))}
      
      {filteredPlantsList.length === 0 && (
        <div className="dropdown-option disabled">
          No Plants Found
        </div>
      )}
    </div>
  )}
</div>
        </div>

        {/* Toggle switch for Show alerts only */}
        
      </div>
      <hr></hr>
      {/* Top bar with tabs */}
      <div className="summary-topbar">
        <div className="summary-tabs">
          <span
            className={`summary-tab${activeTab === 'assets' ? ' active' : ''}`}
            onClick={() => handleTabChange('assets')}
            style={{ cursor: 'pointer' }}
          >
            Assets <span className="summary-badge">{assetsCount}</span>
          </span>
          <span
            className={`summary-tab${activeTab === 'pinned' ? ' active' : ''}`}
            onClick={() => handleTabChange('pinned')}
            style={{ cursor: 'pointer' }}
          >
            Pinned <span className="summary-badge pinned">{pinnedAssetsCount}</span>
          </span>

          <span
            className={`summary-tab${activeTab === 'mapview' ? ' active' : ''}`}
            onClick={() => handleTabChange('mapview')}
            style={{ cursor: 'pointer' }}
          >
            Map View
          </span>
        </div>
        <div className='header-right'>


          {/* Hide toggle and asset type dropdown in mapview tab */}
          {activeTab !== 'mapview' && (
            <>
              <div className="summary-toggle-group">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={showAlertsOnly}
                    onChange={handleAlertsToggleChange}
                  />
                  <span className="slider-add round"></span>
                </label>
                <span className="summary-toggle-text">Show Alerts Only</span>
              </div>
              {/* Asset type searchable dropdown */}
              <div className="searchable-select-container-summary">
                <div className="searchable-select-input-summary" onClick={() => { const next = !isAssetTypeOpen; setIsAssetTypeOpen(next); setIsCustomerOpen(false); setIsSiteOpen(false); setIsPlantOpen(false); }}>
                  <input
                    type="text"
                    value={isAssetTypeOpen ? assetTypeSearch : (selectedAssetType || '')}
                    onChange={(e) => setAssetTypeSearch(e.target.value)}
                    onFocus={() => { /* no-op to avoid first-click open/close race */ }}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        setIsAssetTypeOpen(true);
                      }
                    }}
                    placeholder={isAssetTypeOpen ? 'Search Asset Types...' : (selectedAssetType || 'Asset Type - All')}
                    className="summary-select searchable-input placeholder-white"
                    readOnly={!isAssetTypeOpen}
                  />
                  <span className={`dropdown-arrow-Asset ${isAssetTypeOpen ? 'open' : ''}`}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                               </svg></span>
                </div>
                {isAssetTypeOpen && (
                  <div className="searchable-dropdown">
                    <div
                      className={`dropdown-option ${selectedAssetType === '' ? 'selected' : ''}`}
                      onClick={() => { setSelectedAssetType(''); setIsAssetTypeOpen(false); setAssetTypeSearch(''); setCurrentPage(1); }}
                    >
                      Asset Type - All
                    </div>
                    {filteredAssetTypesList.map((type: any, index: number) => (
                      <div
                        key={`${type.typeName}-${index}`}
                        className={`dropdown-option ${type.typeName === selectedAssetType ? 'selected' : ''}`}
                        onClick={() => { setSelectedAssetType(type.typeName); setIsAssetTypeOpen(false); setAssetTypeSearch(''); setCurrentPage(1); }}
                      >
                    {type.typeName}
                      </div>
                ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

      </div>
      {/* Download and view buttons */}
      <div className="summary-content-body">
        <div className="summary-table-container">
          <div className="summary-searchbar-row">
            <div className='summary-searchbar-group'>
              <img src={SearchIcon} alt='Search' className='search-icons' />
              <input
                type='text'
                className="~summary-searchbar search-input-plant search-input-summary"
                placeholder="Search"
                value={searchText}
                onChange={handleSearchChange}
              />
            </div>
            {activeTab !== 'mapview' && (
              //   Download
              <button 
  className="summary-download-btn" 
  onClick={handleDownloadCSV}
  disabled={isDownloadDisabled}
  style={isDownloadDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
>
  <img className="download-icon-lg" src={DownloadIcon} alt="Download" />
  Download
</button>
            )}
          </div>

          {activeTab === 'assets' ? (
            <div className="summary-table-scroll">
              <table className="global-table summary-table summary-sticky" style={{overflow:'scroll'}}>
                <thead style={{ position:'sticky',top:'0',zIndex:'100'}}>
                  <tr>
                    <th className="col-narrow" style={{zIndex:'5'}}></th>
                     <th className="col-large-assetname" onClick={() => requestSort('assetName')}>
                      <div className="sort-header">
                        <p className='data-text' >Asset Name</p>
                        {getSortArrow('assetName')}
                      </div>
                    </th>
                    <th className="col-customer-summary" onClick={() => requestSort('customer')}>
                      <div className="sort-header">
                        <p className='data-text' >Customer</p>
                        {getSortArrow('customer')}
                      </div>
                    </th>
                    <th className="col-site-summary" onClick={() => requestSort('site')}>
                      <div className="sort-header">
                        <p className='data-text' >Site</p>
                        {getSortArrow('site')}
                      </div>
                    </th>
                    <th className="col-plant-summary" onClick={() => requestSort('plant')}>
                      <div className="sort-header">
                        <p className='data-text' >Plant</p>
                        {getSortArrow('plant')}
                      </div>
                    </th>
                    <th className="col-medium" onClick={() => requestSort('assetType')}>
                      <div className="sort-header">
                        <p className='data-text' >Asset Type</p>
                        {getSortArrow('assetType')}
                      </div>
                    </th>
                     <th className="col-status" onClick={() => requestSort('status')}>
                      <div className="sort-header">
                        <p className='data-text' >Asset Status</p>
                        {getSortArrow('status')}
                      </div>
                    </th>
                    <th className="col-medium">
                      <div className="sort-header">
                        <p className='data-text' >
                          Gateway Status
                          <div className="info-icon-wrapper" style={{display: 'inline-block', position: 'relative'}}
                            onMouseEnter={(e) => {
                              const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                              if (tooltip) {
                                const rect = e.currentTarget.getBoundingClientRect();
                                tooltip.style.position = 'fixed';
                                tooltip.style.left = `${rect.left + (rect.width / 2)}px`;
                                tooltip.style.top = `${rect.top - 18}px`;
                                tooltip.style.transform = 'translate(-50%, -100%)';
                                tooltip.style.opacity = '1';
                                tooltip.style.visibility = 'visible';
                              }
                            }}
                            onMouseLeave={(e) => {
                              const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                              if (tooltip) {
                                tooltip.style.opacity = '0';
                                tooltip.style.visibility = 'hidden';
                              }
                            }}
                          >
                            <img src={InfoIcon} alt="Info" className="info-icon" onClick={(e) => e.stopPropagation()} style={{marginLeft: '5px', width: '16px', height: '16px', verticalAlign: 'middle', filter: 'brightness(0) saturate(100%) invert(47%) sepia(100%) saturate(2000%) hue-rotate(15deg) brightness(100%) contrast(100%)', cursor: 'pointer'}} />
                            <div className="custom-tooltip" style={{
                              position: 'fixed',
                              backgroundColor: '#4A5463',
                              color: 'white',
                              padding: '8px 12px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              opacity: '0',
                              visibility: 'hidden',
                              transition: 'opacity 0.3s, visibility 0.3s',
                              zIndex: '9999',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              maxWidth: '250px',
                              whiteSpace: 'normal',
                              lineHeight: '1.2',
                              pointerEvents: 'none'
                            }}>
                              Connected/Disconnected to Azure IoT Hub. This is gathered from IoT Hub device twin.
                            </div>
                          </div>
                        </p>
                      </div>
                    </th>
                    <th className="col-alert" onClick={() => requestSort('alertsCount')}>
                      <div className="sort-header">
                        <p className='data-text' >Alerts</p>
                        {getSortArrow('alertsCount')}
                      </div>
                    </th>
                    <th className="col-medium" onClick={() => requestSort('lastCommunication')}>
                      <div className="sort-header">
                        <p className='data-text' >Last Data Received</p>
                        {getSortArrow('lastCommunication')}
                      </div>
                    </th>
                    <th className="col-medium" onClick={() => requestSort('firstCommunication')}>
                      <div className="sort-header">
                        <p className='data-text' >Commissioned On</p>
                        {getSortArrow('firstCommunication')}
                      </div>
                    </th>
                    
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="table-loading">
                        <Loader />
                      </td>
                    </tr>
                  ) : Array.isArray(pagedAssetSummary) && pagedAssetSummary.length > 0 ? (
                    pagedAssetSummary.map((row: any, idx: number) => (
                      <tr key={row.id || `${row.assetId}-${idx}` || `asset-${idx}`}>
                        <td className='summary-pin' style={{zIndex:'5',backgroundColor:'#101c2b'}}>
                          <span
                            className="summary-pin-icon"
                            style={{
                              backgroundImage: `url(${Boolean(pinnedAssetsMap[row.assetId]) ? pinColor : pinWhite})`,
                              cursor: pinningAssetId === row.assetId ? 'wait' : 'pointer',
                            }}
                            onClick={() => handlePinClick(row)}
                          />
                        </td>
                        <td
                          className="asset-link"
                          
                          onClick={() => handleAssetNameClick(row)}
                          style={{ cursor: 'pointer',backgroundColor:'#101c2b' }}
                          title={row.assetName || ''}
                        >
                          {row.assetName || ''}
                        </td>
                        <td className="text-wrap-customer" title={row.customerName || ''}>{row.customerName || ''}</td>
                        <td className="text-wrap-site" title={row.siteName || ''}>{row.siteName || ''}</td>
                        <td className="text-wrap-plant" title={row.plantName || ''}>{row.plantName || ''}</td>
                        <td className="text-wrap-assettype" title={row.assetType || ''}>{row.assetType || ''}</td>
                        
                          <td 
                      className={`summary-status ${
                        row.status?.toLowerCase() === 'active' || row.status?.toLowerCase() === 'enabled' ? 'status-active' : 
                        row.status?.toLowerCase() === 'inactive' ? 'status-inactive' : 
                        row.status?.toLowerCase() === 'disabled' ? 'status-disable' : ''
                      }`}
                      title={row.status || ''}
                    >
                      <p className='status-text'>{row.status || ''}</p>
                    </td>
                       <td className="summary-gateway-status">
                      {row.gatewayDeviceId ? (
                        <span
                          className="content-link"
                          onClick={() => handleGatewayStatusClick(row)}
                          style={{ cursor: 'pointer' }}
                        >
                          Device Status
                        </span>
                      ) : (
                        <span>N/A</span>
                      )}
                    </td>
                        <td className='summary-alertss'>
                          {(() => {
                            const alertsCount = parseInt(row.alertsCount) || 0;
                            const hasAlerts = Array.isArray(row.alerts) && row.alerts.length > 0;
                            const hasAlertsCount = alertsCount > 0;

                            if (hasAlerts || hasAlertsCount) {
                              return (
                                <div
                                  className="alerts-container"
                                  onClick={() => handleAlertsClick(row)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <span className="content-link alert-badge">
                                    {row.alertsCount || row.alerts.length}
                                  </span>
                                </div>
                              );
                            }
                            return null; // Don't show anything when count is 0
                          })()}
                        </td>
                        <td className="text-wrap" title={formatLastCommunication(row.lastCommunication)}>
                          {formatLastCommunication(row.lastCommunication)}
                        </td>
                        <td className="text-wrap" title={formatFirstCommunication(row.firstCommunication)}>
                          {formatFirstCommunication(row.firstCommunication)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={11} className="table-empty">No Data Available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) :activeTab === 'pinned' ? (
  <div className="summary-table-scroll">
    <table className="global-table summary-table summary-sticky" style={{overflow:'scroll'}}>
      <thead style={{ position:'sticky',top:'0',zIndex:'100'}}>
        <tr>
          <th className="col-narrow"></th>
          <th className="col-large-assetname" onClick={() => requestSort('assetName')}>
            <div className="sort-header">
              <p className='data-text'>Asset Name</p>
              {getSortArrow('assetName')}
            </div>
          </th>
          <th className="col-customer-summary" onClick={() => requestSort('customer')}>
            <div className="sort-header">
              <p className='data-text'>Customer</p>
              {getSortArrow('customer')}
            </div>
          </th>
          <th className="col-site-summary" onClick={() => requestSort('site')}>
            <div className="sort-header">
              <p className='data-text'>Site</p>
              {getSortArrow('site')}
            </div>
          </th>
          <th className="col-plant-summary" onClick={() => requestSort('plant')}>
            <div className="sort-header">
              <p className='data-text'>Plant</p>
              {getSortArrow('plant')}
            </div>
          </th>
          <th className="col-medium" onClick={() => requestSort('assetType')}>
            <div className="sort-header">
              <p className='data-text'>Asset Type</p>
              {getSortArrow('assetType')}
            </div>
          </th>
          <th className="col-status" onClick={() => requestSort('status')}>
            <div className="sort-header">
              <p className='data-text'> Asset Status</p>
              {getSortArrow('status')}
            </div>
          </th>
          <th className="col-medium">
            <div className="sort-header">
              <p className='data-text'>
                Gateway Status
                <div className="info-icon-wrapper" style={{display: 'inline-block', position: 'relative'}}
                  onMouseEnter={(e) => {
                    const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                    if (tooltip) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      tooltip.style.position = 'fixed';
                      tooltip.style.left = `${rect.left + (rect.width / 2)}px`;
                      tooltip.style.top = `${rect.top - 18}px`;
                      tooltip.style.transform = 'translate(-50%, -100%)';
                      tooltip.style.opacity = '1';
                      tooltip.style.visibility = 'visible';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const tooltip = e.currentTarget.querySelector('.custom-tooltip') as HTMLElement;
                    if (tooltip) {
                      tooltip.style.opacity = '0';
                      tooltip.style.visibility = 'hidden';
                    }
                  }}
                >
                  <img src={InfoIcon} alt="Info" className="info-icon" onClick={(e) => e.stopPropagation()} style={{marginLeft: '5px', width: '16px', height: '16px', verticalAlign: 'middle', filter: 'brightness(0) saturate(100%) invert(47%) sepia(100%) saturate(2000%) hue-rotate(15deg) brightness(100%) contrast(100%)', cursor: 'pointer'}} />
                  <div className="custom-tooltip" style={{
                    position: 'fixed',
                    backgroundColor: '#4A5463',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    opacity: '0',
                    visibility: 'hidden',
                    transition: 'opacity 0.3s, visibility 0.3s',
                    zIndex: '9999',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    maxWidth: '250px',
                    whiteSpace: 'normal',
                    lineHeight: '1.2',
                    pointerEvents: 'none'
                  }}>
                    Connected/Disconnected to Azure IoT Hub. This is gathered from IoT Hub device twin.
                  </div>
                </div>
              </p>
            </div>
          </th>
          <th className="col-alert" onClick={() => requestSort('alertsCount')}>
            <div className="sort-header">
              <p className='data-text'>Alerts</p>
              {getSortArrow('alertsCount')}
            </div>
          </th>
          <th className="col-medium" onClick={() => requestSort('lastCommunication')}>
            <div className="sort-header">
              <p className='data-text'>Last Data Received</p>
              {getSortArrow('lastCommunication')}
            </div>
          </th>
          <th className="col-medium" onClick={() => requestSort('firstCommunication')}>
            <div className="sort-header">
              <p className='data-text'>Commissioned On</p>
              {getSortArrow('firstCommunication')}
            </div>
          </th>
        </tr>
      </thead>
                <tbody>
                  {pinnedPageLoading ? (
                    <tr>
                      <td colSpan={11} className="table-loading">
                        <Loader />
                      </td>
                    </tr>
                  ) : Array.isArray(pagedPinnedRows) && pagedPinnedRows.length > 0 ? (
                    pagedPinnedRows.map((row: any, idx: number) => (
                      <tr key={row.id || `${row.assetId}-${idx}` || `pinned-${idx}`}>
                        <td className="summary-pin" style={{background:'#0F1D2E',zIndex:'10'}}>
                          <span
                            className="summary-pin-icon"
                            style={{
                              backgroundImage: `url(${pinColor})`,
                              cursor: pinningAssetId === row.assetId ? 'wait' : 'pointer',
                            }}
                            onClick={() => handlePinClick(row)}
                          />
                        </td>
                        <td
                          className="asset-link"
                          onClick={() => handleAssetNameClick(row)}
                          style={{ cursor: 'pointer',backgroundColor:'#0F1D2E' }}
                          title={row.assetName || ''}
                        >
                          {row.assetName || ''}
                        </td>
                        <td className="text-wrap-customer" title={row.customerName || ''}>{row.customerName || ''}</td>
                        <td className="text-wrap-site" title={row.siteName || ''}>{row.siteName || ''}</td>
                        <td className="text-wrap-plant" title={row.plantName || ''}>{row.plantName || ''}</td>
                        <td className="text-wrap-assettype" title={row.assetType || ''}>{row.assetType || ''}</td>
                        
                        <td 
                        className={`summary-status ${
                          row.status?.toLowerCase() === 'active' || row.status?.toLowerCase() === 'enabled' ? 'status-active' : 
                          row.status?.toLowerCase() === 'inactive' ? 'status-inactive' : 
                          row.status?.toLowerCase() === 'disabled' ? 'status-disable' : ''
                        }`}
                        title={row.status || ''}
                        >
                        <p className='status-text'>{row.status || ''}</p>
                        </td>
                      <td className="summary-gateway-status">
                      {row.gatewayDeviceId ? (
                        <span
                          className="content-link"
                          onClick={() => handleGatewayStatusClick(row)}
                          style={{ cursor: 'pointer' }}
                        >
                          Gateway Status
                        </span>
                      ) : (
                        <span>N/A</span>
                      )}
                    </td>
                        <td className='summary-alertss'>
                          {(() => {
                            const alertsCount = parseInt(row.alertsCount) || 0;
                            const hasAlerts = Array.isArray(row.alerts) && row.alerts.length > 0;
                            const hasAlertsCount = alertsCount > 0;

                            if (hasAlerts || hasAlertsCount) {
                              return (
                                <div
                                  className="alerts-container"
                                  onClick={() => handleAlertsClick(row)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <span className="content-link alert-badge summary-alertss">
                                    {row.alertsCount || row.alerts.length}
                                  </span>
                                </div>
                              );
                            }
                            return null; // Don't show anything when count is 0
                          })()}
                        </td>
                        <td className="text-wrap" title={formatLastCommunication(row.lastCommunication)}>
                          {formatLastCommunication(row.lastCommunication)}
                        </td>
                        <td className="text-wrap" title={formatFirstCommunication(row.firstCommunication)}>
                          {formatFirstCommunication(row.firstCommunication)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={11}>No Data Available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : null}

          {activeTab === 'mapview' && (
            <div className="summary-map-container">
              <MapView 
                selectedCustomerId={selectedCustomerId || undefined}
                selectedSiteId={selectedSiteId || undefined}
                selectedPlantId={selectedPlantId || undefined}
              />
            </div>
          )}

          {(activeTab === 'assets' || activeTab === 'pinned') && (
            <div className="summary-pagination">
              <button
                className="summary-pagination-prev"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
               <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginBottom:"-5px"}}>
               <path d="M15.8333 10L4.16668 10M4.16668 10L10 15.8334M4.16668 10L10 4.16669" stroke="#FCFCFD" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
               </svg>  {'Previous'}
                
              </button>
              <span>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} {activeTab === 'assets' ? 'Entries' : 'Pinned Assets'}
              </span>
              <button
                className="summary-pagination-next"
                onClick={goToNextPage}
                disabled={currentPage === totalPages || totalPages === 0 || !totalPages}
              >
                {'Next'}  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginBottom:"-5px"}}>
             <path d="M4.16675 9.99996H15.8334M15.8334 9.99996L10.0001 4.16663M15.8334 9.99996L10.0001 15.8333" stroke="#FCFCFD" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
             </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Gateway Status Modal */}
      {showGatewayStatusModal && selectedGatewayStatus && (
        <div className="modal-overlay-gateway-status" onClick={closeGatewayStatusModal}>
          <div className="modal-content-gateway-status gateway-status-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Gateway Status</h3>
              <button className="modal-close-btn" onClick={closeGatewayStatusModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="gateway-status-details">
                <p><strong>Asset Name:</strong> {selectedGatewayStatus.assetName}</p>
                {selectedGatewayStatus.loading ? (
                  <p>Loading gateway status...</p>
                ) : selectedGatewayStatus.error ? (
                  <p>{selectedGatewayStatus.error}</p>
                ) : (
                  <div
                    className={`summary-status ${
                      selectedGatewayStatus.status.toLowerCase() === 'connected'
                        ? 'status-active'
                        : selectedGatewayStatus.status.toLowerCase() === 'disconnected'
                        ? 'status-inactive'
                        : ''
                    }`}
                  >
                    <p className="status-text">{selectedGatewayStatus.status}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeGatewayStatusModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alerts Modal */}
      {showAlertsModal && selectedAssetAlerts && (
        <div className="modal-overlay" onClick={closeAlertsModal}>
          <div className="modal-content-alert alerts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Alerts for {selectedAssetAlerts.assetName}</h3>
              <button className="modal-close-btn" onClick={closeAlertsModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="alerts-list alerts-list-scrollable">
                {selectedAssetAlerts.alerts && selectedAssetAlerts.alerts.length > 0 ? (
                  selectedAssetAlerts.alerts.map((alert, index) => (
                    <div key={index} className={`alert-item ${getSeverityClass(alert.severity)}`}>
                      {(getSeverityClass(alert.severity) === 'danger' || getSeverityClass(alert.severity) === 'critical') && (
                        <span className="alert-icon">
                          <img src={DangerRedIcon} alt="Danger" width={40} height={40} />
                        </span>
                      )}
                      {getSeverityClass(alert.severity) === 'warning' && (
                        <span className="alert-icon">
                          <img src={WarningYellowIcon} alt="Warning" width={40} height={40} />
                        </span>
                      )}
                      <div className="alert-details">
                        <div className="alert-message">
                          <span>Reason:</span> {
                            alert.reason ? alert.reason : (
                              alert.value >= alert.thresholdHH ? 'High High' :
                              (alert.value >= alert.thresholdH && alert.value < alert.thresholdHH) ? 'High':
                              alert.value <= alert.thresholdLL ? 'Low Low' :
                              (alert.value <= alert.thresholdL && alert.value > alert.thresholdLL) ? 'Low' :
                              'N/A'
                            )
                          }
                        </div>
                        <div className="alert-description">
                          <strong>Attribute:</strong> {alert.attribute || 'N/A'}
                          {alert.value !== undefined && (
                            <span> | <strong>Value:</strong> {alert.value}</span>
                          )}
                        </div>
                        <div className="alert-timestamp">
                          <strong>Alert Time:</strong> {formatAlertTime(alert.alertTime)}
                        </div>
                      </div>
                      <span className={`alert-severity ${getSeverityClass(alert.severity)}`}>
                        {alert.severity || 'Unknown'}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="no-alerts-message">
                    <span className="alert-icon">ℹ️</span>
                    <p>No alerts found for this asset.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeAlertsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Summary;
